import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, ExternalLink } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import { formatPhone } from "../components/Header";
import { track } from "../lib/api";
import { PageHero } from "./AboutPage";
import { Seo } from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

export default function ContactPage() {
  const { settings, L } = useSite();
  const rows = [
    { icon: Phone, label: L.contact.phone, value: formatPhone(settings.phone), href: `tel:${settings.phone}`, event: "phone_click", testid: "contact-phone" },
    settings.secondary_phone && { icon: Phone, label: L.contact.phone2, value: formatPhone(settings.secondary_phone), href: `tel:${settings.secondary_phone}`, event: "phone_click", testid: "contact-phone-2" },
    { icon: Mail, label: L.contact.email, value: settings.email, href: `mailto:${settings.email}`, event: "email_click", testid: "contact-email" },
    { icon: MapPin, label: L.contact.area, value: settings.service_area, testid: "contact-area" },
    settings.business_hours && { icon: Clock, label: L.contact.hours, value: settings.business_hours, testid: "contact-hours" },
  ].filter(Boolean);

  return (
    <main data-testid="contact-page">
      <Seo title={PAGE_SEO.contact.title} description={PAGE_SEO.contact.description} path="/contact" />
      <PageHero overline={L.contact.overline} title={L.contact.title}
        subtitle={L.contact.subtitle} />
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="bg-white border border-slate-200 p-8 md:p-12 space-y-7">
              {rows.map((r) => (
                <div key={r.label} className="flex items-start gap-4" data-testid={r.testid}>
                  <span className="w-11 h-11 bg-[#0B1B3D] flex items-center justify-center shrink-0"><r.icon className="w-5 h-5 text-[#4D8DFF]" /></span>
                  <div>
                    <p className="field-label !mb-1">{r.label}</p>
                    {r.href ? (
                      <a href={r.href} onClick={() => track(r.event)} className="font-heading font-bold text-[#0B1B3D] text-lg hover:text-[#005AE0] transition-colors">{r.value}</a>
                    ) : (
                      <p className="font-heading font-bold text-[#0B1B3D] text-lg">{r.value}</p>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                {settings.facebook_url && (
                  <a href={settings.facebook_url} target="_blank" rel="noreferrer" data-testid="contact-facebook" onClick={() => track("facebook_click")}
                    className="w-11 h-11 border border-slate-300 flex items-center justify-center text-[#0B1B3D] hover:bg-[#005AE0] hover:text-white hover:border-[#005AE0] transition-colors"><Facebook className="w-5 h-5" /></a>
                )}
                {settings.nextdoor_url && (
                  <a href={settings.nextdoor_url} target="_blank" rel="noreferrer" data-testid="contact-nextdoor" onClick={() => track("nextdoor_click")}
                    className="w-11 h-11 border border-slate-300 flex items-center justify-center text-[#0B1B3D] hover:bg-[#005AE0] hover:text-white hover:border-[#005AE0] transition-colors"><ExternalLink className="w-5 h-5" /></a>
                )}
                {settings.google_business_url && (
                  <a href={settings.google_business_url} target="_blank" rel="noreferrer" data-testid="contact-google" onClick={() => track("google_business_click")}
                    className="w-11 h-11 border border-slate-300 flex items-center justify-center text-[#0B1B3D] hover:bg-[#005AE0] hover:text-white hover:border-[#005AE0] transition-colors"><MapPin className="w-5 h-5" /></a>
                )}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-[#0B1B3D] p-8 md:p-12 h-full flex flex-col justify-center relative grain">
              <h2 className="font-heading font-extrabold text-white text-3xl mb-4">{L.contact.boxTitle}</h2>
              <p className="text-slate-400 leading-relaxed mb-10">
                {L.contact.boxText}
              </p>
              <div className="flex flex-col gap-4">
                <a href={`tel:${settings.phone}`} data-testid="contact-call-now-btn" onClick={() => track("phone_click", { location: "contact_page" })} className="btn-primary w-full">
                  <Phone className="w-4 h-4" /> {L.contact.call}
                </a>
                <Link to="/estimate" data-testid="contact-estimate-btn" className="btn-outline w-full">{L.contact.request}</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
